import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { buildQueryParams } from "@finitless/shared/lib";

const PAGE_SIZE = 25;

/**
 * Build where clause for business search
 * @param {string} searchTerm - Search term for business name
 * @returns {Object|null} Where clause object or null if no search term
 */
const buildBusinessSearchWhereClause = (searchTerm) => {
  if (!searchTerm || searchTerm.trim() === "") {
    return null;
  }

  return {
    conditions: [
      {
        attribute: "name",
        value: {
          condition: "ilike",
          value: `%${searchTerm.trim()}%`,
        },
      },
    ],
    conector: "OR",
  };
};

/**
 * Hook to get businesses lite with infinite scroll and optional search
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Whether the query is enabled (default: true)
 * @param {string} options.searchTerm - Search term for business name (default: '')
 * @returns {Object} Infinite query result with flattened businesses array
 */
export const useBusinessesLite = ({ enabled = true, searchTerm = "" } = {}) => {
  const whereClause = buildBusinessSearchWhereClause(searchTerm);

  const query = useInfiniteQuery({
    queryKey: ["businesses-lite", searchTerm],
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = {
        params: "name,address",
        page: pageParam,
        page_size: PAGE_SIZE,
      };

      if (whereClause) {
        queryParams.where = whereClause;
      }

      const queryString = buildQueryParams(queryParams);
      const response = await apiClient.get(
        `/backoffice/businesses${queryString ? `?${queryString}` : ""}`,
      );

      return {
        businesses: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      const currentPage = pagination.current_page || pagination.page || 1;
      const totalPages = pagination.total_pages || 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled: !!enabled && isEndpointEnabled("business", "list"),
    staleTime: 5 * 60 * 1000,
  });

  // Flatten all pages into a single businesses array
  const businesses =
    query.data?.pages?.flatMap((page) => page.businesses) || [];

  return {
    ...query,
    businesses,
  };
};

export default {
  useBusinessesLite,
};
